import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, FileText, Square, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { ChatMarkdown } from './ChatMarkdown';
import { ChatMessageActions } from './ChatMessageActions';
import { RichInput } from './RichInput';
import {
    CreatedDocumentCard, EditedDocumentCard, CreatedMockupCard, EditedMockupCard,
} from './AgentEditorPanel';
import { TaskChangesChip } from './TaskChangesChip';
import { ToolSkeletonCard } from './ToolSkeletonCard';
import { ChatErrorTurn } from './ChatErrorTurn';

// ═══════════════════════════════════════════════════════════════════════════════
// CHAT MESSAGE — "transcript" layout
//
// The assistant does not get a bubble. Its turn is full-column prose behind a
// thin rail, headed by a mono meta line. That is what gives markdown, code
// blocks and result cards the whole reading measure; the old shared bubble
// capped every reply at 75% of an already narrow column.
//
// The user keeps a bubble, right-aligned and capped near 60ch, so the
// back-and-forth rhythm survives without avatar chrome on every row.
// ═══════════════════════════════════════════════════════════════════════════════

export const MESSAGE_SPRING = { type: 'spring', stiffness: 260, damping: 28 };

/** "820ms" · "3.4s" · "1m 04s" */
export function formatElapsed(ms) {
    if (ms == null) return null;
    if (ms < 1000) return `${Math.round(ms)}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    const m = Math.floor(ms / 60000);
    const s = Math.round((ms % 60000) / 1000);
    return `${m}m ${String(s).padStart(2, '0')}s`;
}

/** The agent's identity mark + whatever we truthfully know about the turn. */
export function AgentMetaLine({ modelLabel, elapsedMs, children, className }) {
    const elapsed = formatElapsed(elapsedMs);
    return (
        <div
            className={cn(
                'mb-2 flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground',
                className
            )}
        >
            <span className="flex h-5 w-5 items-center justify-center rounded-md border border-primary/30 bg-gradient-to-br from-primary/30 to-accent/20">
                <Sparkles className="h-3 w-3 text-primary" />
            </span>
            <span className="text-foreground/70">Forge</span>
            {modelLabel && (
                <>
                    <span className="opacity-40">·</span>
                    <span>{modelLabel}</span>
                </>
            )}
            {elapsed && (
                <>
                    <span className="opacity-40">·</span>
                    <span className="tabular-nums">{elapsed}</span>
                </>
            )}
            {children}
        </div>
    );
}

/**
 * The marker left behind when the user stops a generation.
 *
 * Aborting the request does not abort the server: the backend persists both
 * messages once Gemini returns. So this offers a reload rather than pretending
 * the turn never happened.
 */
function StoppedTurn({ onReload }) {
    return (
        <div className="flex items-center gap-2 border-l-2 border-white/10 pl-4 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground md:pl-5">
            <Square className="h-3 w-3 fill-current" />
            Stopped
            {onReload && (
                <button
                    onClick={onReload}
                    data-testid="chat-reload-after-stop"
                    className="ml-1 flex items-center gap-1 font-sans text-[11px] font-normal normal-case tracking-normal text-primary/80 transition-colors hover:text-primary"
                >
                    <RotateCcw className="h-3 w-3" />
                    Reload conversation
                </button>
            )}
        </div>
    );
}

/** Images and file chips the user attached to their own message. */
function Attachments({ attachments }) {
    const images = attachments.filter(a => a.type === 'image');
    const others = attachments.filter(a => a.type !== 'image');

    return (
        <div className="mb-3 space-y-2">
            {images.length > 0 && (
                <div className={images.length === 1 ? '' : 'grid grid-cols-2 gap-1.5'}>
                    {images.map((attachment, idx) => (
                        <img
                            key={`img-${idx}`}
                            src={attachment.dataUrl}
                            alt={attachment.name || 'Attached image'}
                            className={cn(
                                'w-full rounded-lg border border-white/20 object-cover',
                                images.length === 1 ? 'max-h-64' : 'h-32'
                            )}
                        />
                    ))}
                </div>
            )}
            {others.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {others.map((attachment, idx) => (
                        <div
                            key={`file-${idx}`}
                            className="flex items-center gap-1.5 rounded-lg border border-white/30 bg-white/20 px-2 py-1 text-xs"
                        >
                            <FileText className="h-3 w-3 opacity-80" />
                            <span className="max-w-[120px] truncate font-medium">{attachment.name}</span>
                            {attachment.size && (
                                <span className="text-[10px] opacity-60">
                                    {attachment.size > 1024 * 1024
                                        ? `${(attachment.size / (1024 * 1024)).toFixed(1)}MB`
                                        : `${(attachment.size / 1024).toFixed(0)}KB`}
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/**
 * In-place editor replacing a user message bubble when 'Edit' or 'Branch and edit'
 * is activated (matches ChatGPT-style inline edit card with Cancel and Send).
 */
function UserMessageEditor({
    originalContent,
    value,
    onChange,
    onCancel,
    onSubmit,
    disabled = false,
}) {
    const inputRef = useRef(null);

    useEffect(() => {
        const frame = requestAnimationFrame(() => {
            if (inputRef.current?.focusEnd) {
                inputRef.current.focusEnd();
            } else {
                inputRef.current?.focus?.();
            }
        });
        return () => cancelAnimationFrame(frame);
    }, []);

    const trimmedValue = (value || '').trim();
    const trimmedOriginal = (originalContent || '').trim();
    const canSubmit = trimmedValue.length > 0 && trimmedValue !== trimmedOriginal && !disabled;

    return (
        <div
            data-testid="chat-inline-editor"
            className="w-full max-w-[min(68ch,95%)] rounded-2xl border border-white/15 bg-secondary/80 px-4 pt-3.5 pb-3 shadow-xl backdrop-blur-md transition-all focus-within:border-primary/40"
        >
            <RichInput
                ref={inputRef}
                value={value}
                onChange={(e) => onChange?.(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                        e.preventDefault();
                        e.stopPropagation();
                        onCancel?.();
                    } else if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        if (e.nativeEvent?.isComposing || e.isComposing || e.keyCode === 229) return;
                        if (canSubmit) onSubmit?.();
                    }
                }}
                disabled={disabled}
                placeholder="Edit your message..."
                className="min-h-[44px] max-h-[240px] w-full border-0 bg-transparent px-0 py-0 text-[15px] leading-[1.65] text-foreground shadow-none focus-visible:outline-none"
            />
            <div className="mt-3 flex items-center justify-end gap-2">
                <button
                    type="button"
                    onClick={onCancel}
                    disabled={disabled}
                    data-testid="chat-edit-cancel"
                    className="rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-white/10 disabled:opacity-50"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    onClick={onSubmit}
                    disabled={!canSubmit}
                    data-testid="chat-edit-send"
                    className="rounded-full bg-foreground px-4 py-1.5 text-xs font-semibold text-background transition-all hover:bg-foreground/90 disabled:pointer-events-none disabled:opacity-40"
                >
                    Send
                </button>
            </div>
        </div>
    );
}

function ChatMessageInner({
    message,
    messageIndex,
    files = [],
    tasks = [],
    readOnly = false,
    baseUrl = '',
    navigate,
    createdFile, editedFile, createdMockup, editedMockup,
    taskChanges,
    pendingTools,
    turnMeta,
    entranceDelay = 0,
    reduceMotion = false,
    isLastUserMessage = true,
    isEditing = false,
    editingText = '',
    onEditChange,
    onStartEdit,
    onCancelEdit,
    onSubmitEdit,
    onBranchAndEdit,
    isSubmittingEdit = false,
    onRetry, onEditAndResend, onReload, onDismissError,
    onOpenTaskChanges, onOpenAgentFile, onOpenInFullEditor, onViewDiff, onOpenEditedInFullEditor,
    onOpenMockupPreview, onOpenMockupInEditor, onViewMockupDiff, onOpenEditedMockupInEditor,
}) {
    const isUser = message.role === 'user';

    const handleReferenceClick = useCallback((type, name) => {
        if (readOnly) {
            toast.info(`Sign up to explore ${type.toLowerCase()}s in detail!`);
            return;
        }
        if (type === 'File') {
            const file = files.find(f => f.name === name);
            if (file) navigate(`${baseUrl}/editor/${file.id}`);
            else toast.error(`File "${name}" not found in this project`);
        } else if (type === 'Task') {
            const task = tasks.find(t => t.title === name);
            if (task) {
                navigate(`${baseUrl}/tasks`);
                toast.success(`Navigating to task: ${name}`);
            } else {
                toast.error(`Task "${name}" not found`);
            }
        }
    }, [readOnly, files, tasks, navigate, baseUrl]);

    const motionProps = useMemo(() => ({
        initial: reduceMotion ? false : { opacity: 0, y: 12 },
        animate: { opacity: 1, y: 0 },
        transition: { ...MESSAGE_SPRING, delay: entranceDelay },
    }), [reduceMotion, entranceDelay]);

    // ─── Failed turn ─────────────────────────────────────────────────────────
    // Sits where the answer would have been, so the transcript never just stops
    // after the user's prompt with nothing to explain it.
    if (message.error) {
        return (
            <motion.div {...motionProps} className="group/msg w-full">
                <ChatErrorTurn
                    error={message.error}
                    onRetry={onRetry}
                    onDismiss={onDismissError}
                    onReload={message.error.kind === 'timeout' ? onReload : undefined}
                />
            </motion.div>
        );
    }

    // ─── Stopped marker ──────────────────────────────────────────────────────
    if (message.stopped) {
        return (
            <motion.div {...motionProps} className="group/msg w-full">
                <StoppedTurn onReload={onReload} />
            </motion.div>
        );
    }

    // ─── User turn ───────────────────────────────────────────────────────────
    if (isUser) {
        if (isEditing) {
            return (
                <motion.div {...motionProps} className="group/msg flex w-full flex-col items-end">
                    <UserMessageEditor
                        originalContent={message.content}
                        value={editingText}
                        onChange={onEditChange}
                        onCancel={onCancelEdit}
                        onSubmit={onSubmitEdit}
                        disabled={isSubmittingEdit}
                    />
                </motion.div>
            );
        }

        return (
            <motion.div {...motionProps} className="group/msg flex w-full flex-col items-end">
                <div className="max-w-[min(60ch,88%)] rounded-2xl rounded-tr-md border border-primary/25 bg-primary/[0.12] px-4 py-3 text-[15px] leading-[1.65] text-foreground">
                    {message.attachments?.length > 0 && <Attachments attachments={message.attachments} />}
                    <ChatMarkdown
                        content={message.content}
                        isUser
                        references={message.references}
                        onReferenceClick={handleReferenceClick}
                    />
                </div>
                <ChatMessageActions
                    content={message.content}
                    isUser
                    isLastUserMessage={isLastUserMessage}
                    onEdit={onStartEdit}
                    onBranchAndEdit={onBranchAndEdit}
                    onEditAndResend={onEditAndResend}
                    className="mt-1 pr-1"
                />
            </motion.div>
        );
    }

    // ─── Assistant turn ──────────────────────────────────────────────────────
    return (
        <motion.div {...motionProps} className="group/msg w-full" data-testid={`chat-message-${messageIndex}`}>
            <AgentMetaLine modelLabel={turnMeta?.modelLabel} elapsedMs={turnMeta?.elapsedMs} />

            <div className="min-w-0 border-l-2 border-primary/40 pl-4 text-[15px] leading-[1.7] text-foreground/90 md:pl-5">
                {message.content && (
                    <ChatMarkdown
                        content={message.content}
                        references={message.references}
                        onReferenceClick={handleReferenceClick}
                    />
                )}

                {/* Skeletons for tools the agent announced but has not finished
                    applying yet. They share geometry with the real cards below
                    so the swap reads as a fill-in, not a replacement. A tool
                    marked done has produced its real card, so it drops out. */}
                {pendingTools?.filter(t => t.status !== 'done').map((tool, idx) => (
                    <ToolSkeletonCard
                        key={`pending-${idx}`}
                        toolName={tool.toolName}
                        target={tool.target}
                        status={tool.status}
                    />
                ))}

                {createdFile && (
                    <CreatedDocumentCard
                        file={createdFile}
                        onOpen={onOpenAgentFile}
                        onOpenInEditor={onOpenInFullEditor}
                    />
                )}

                {editedFile && (
                    <EditedDocumentCard
                        file={editedFile.file}
                        editType={editedFile.editType}
                        editSummary={editedFile.editSummary}
                        isLoading={editedFile.isLoading}
                        loadingStep={editedFile.loadingStep}
                        onViewDiff={onViewDiff}
                        onOpenInEditor={onOpenEditedInFullEditor}
                        isAccepted={editedFile.isAccepted}
                        isStale={editedFile.isStale}
                        isPersisted={editedFile.isPersisted}
                    />
                )}

                {createdMockup && (
                    <CreatedMockupCard
                        file={createdMockup}
                        onOpen={onOpenMockupPreview}
                        onOpenInEditor={onOpenMockupInEditor}
                    />
                )}

                {taskChanges && (
                    <TaskChangesChip
                        summary={taskChanges.summary}
                        total={taskChanges.total ?? 0}
                        active={taskChanges.active ?? 0}
                        noop={taskChanges.noop ?? 0}
                        skipped={taskChanges.skipped ?? 0}
                        conflicted={taskChanges.conflicted}
                        onOpen={onOpenTaskChanges}
                    />
                )}

                {editedMockup && (
                    <EditedMockupCard
                        file={editedMockup.file}
                        editType={editedMockup.editType}
                        editSummary={editedMockup.editSummary}
                        isLoading={editedMockup.isLoading}
                        loadingStep={editedMockup.loadingStep}
                        onViewDiff={onViewMockupDiff}
                        onOpenInEditor={onOpenEditedMockupInEditor}
                        isAccepted={editedMockup.isAccepted}
                        isStale={editedMockup.isStale}
                        isPersisted={editedMockup.isPersisted}
                    />
                )}
            </div>

            <ChatMessageActions
                content={message.content}
                onRetry={onRetry}
                className="mt-1.5 pl-4 md:pl-5"
            />
        </motion.div>
    );
}

export const ChatMessage = React.memo(ChatMessageInner);
export default ChatMessage;
